import {logger} from '../logger';
import {notificationPermissionSignal} from '../signals/Notification.signals';

const toNotificationPermission = (
    state: PermissionState,
): NotificationPermission => (state === 'prompt' ? 'default' : state);

export type NotificationPermissionState =
    NotificationPermission | 'unsupported';

/** Raises system notifications on the surface provided by the host shell. */
export interface NotificationService {
    /** Returns the current permission to show notifications. */
    getPermissionState: () => NotificationPermissionState;
    /** Asks the user to allow notifications when they have not decided yet. */
    requestPermission: () => Promise<void>;
    /**
     * Shows a notification when permission was granted.
     *
     * @param title - Headline shown by the operating system.
     * @param options - Body and tag of the notification.
     *
     * @example
     * service.sendNotification('Alice', {body: 'Alice is calling you'});
     */
    sendNotification: (title: string, options?: NotificationOptions) => void;
}

const initPermission = (): NotificationPermissionState => {
    if (typeof Notification === 'undefined') {
        return 'unsupported';
    }
    return Notification.permission;
};

/**
 * Creates the notification service backed by the Notification Web API.
 *
 * @returns A service whose notifications focus the window when clicked.
 *
 * @example
 * const service = createBrowserNotificationService();
 */
export const createBrowserNotificationService = () => {
    let permission: NotificationPermissionState = initPermission();

    const setPermission = (value: NotificationPermissionState) => {
        if (permission === value) {
            return;
        }
        permission = value;
        notificationPermissionSignal.emit();
    };

    const getPermissionState = () => permission;

    const requestPermission = async () => {
        if (permission !== 'default') {
            return;
        }
        const result = await Notification.requestPermission();
        setPermission(result);
    };

    const sendNotification = (title: string, options?: NotificationOptions) => {
        if (permission !== 'granted') {
            return;
        }
        const notification = new Notification(title, options);
        notification.addEventListener('click', () => {
            window.focus();
            notification.close();
        });
    };

    if (
        typeof navigator !== 'undefined' &&
        typeof navigator.permissions?.query === 'function'
    ) {
        navigator.permissions
            .query({name: 'notifications'})
            .then(result => {
                setPermission(toNotificationPermission(result.state));
                result.onchange = () => {
                    setPermission(toNotificationPermission(result.state));
                };
            })
            .catch(e => {
                logger.info(
                    e,
                    'Browser does not support permissions API for notifications',
                );
            });
    }

    return {
        getPermissionState,
        requestPermission,
        sendNotification,
    };
};
